/**
 * One evidenced-value table — rendered twice, once for income rows and once for
 * expenses. Same anatomy as `LiquiditySourcesTable`: the amount, the UW haircut
 * applied to it, the verified state, and the documents backing it, with a
 * totals row underneath.
 *
 * A row with no `loanDocumentIds` is flagged rather than hidden — UW can save a
 * value before the document arrives, exactly as the liquidity page allows.
 */
import { useMemo } from "react";
import PropTypes from "prop-types";
import { useDispatch } from "react-redux";
import { AgGridReact } from "ag-grid-react";

import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Tooltip from "@mui/material/Tooltip";
import DescriptionOutlined from "@mui/icons-material/DescriptionOutlined";
import RadioButtonUncheckedOutlined from "@mui/icons-material/RadioButtonUncheckedOutlined";
import VerifiedOutlined from "@mui/icons-material/VerifiedOutlined";
import WarningAmberOutlined from "@mui/icons-material/WarningAmberOutlined";
import { amber, green, grey } from "@mui/material/colors";

import { filePreviewSetData } from "../../../../redux/reducers/filePreviewReducer";
import AgGridTableWrapper from "../../../../components/agGrid/AgGridTableWrapper";
import {
  CARD_GRID_ACTIVE_ROW_STYLE,
  CARD_GRID_COL_DEF,
  CARD_GRID_SX,
  CARD_GRID_TWO_LINE_ROW_H,
} from "../../shared/cardGrid";
import Flx from "../../../../components/layout/Flx";
import Txt from "../../../../components/typography/Txt";
import formatDollar from "../../../../_src_shared/utils/functions/formatDollar";
import {
  getComponentMeta,
  GROSS_MONTHLY_RENT,
  leaseAboveMarket,
  listLoanUnits,
  propertyLabelOf,
  rentBasisLabel,
  sourceHasEvidence,
  TABLE_BOX_SX,
} from "./constants";

const docLabel = (doc) =>
  doc?.file_display_name || doc?.fileName || doc?.docType || "Document";

const DscrSourcesTable = ({
  sources,
  loan,
  components,
  onRowClick,
  activeSourceId,
}) => {
  const dispatch = useDispatch();
  const docsById = useMemo(() => {
    const map = new Map();
    (loan?.loanDocuments || []).forEach((d) => map.set(String(d?._id), d));
    return map;
  }, [loan?.loanDocuments]);
  // Rent rows tie to units (`unitId`); expense rows to a property
  // (`subjectPropertyId`). Resolved here so the Source cell can name them.
  const unitsById = useMemo(
    () => new Map(listLoanUnits(loan).map((u) => [String(u.unitId), u])),
    [loan],
  );
  const propertyLabelById = useMemo(
    () =>
      new Map(
        (loan?.subjectProperties || []).map((sp, i) => [
          String(sp?._id),
          propertyLabelOf(sp, i),
        ]),
      ),
    [loan?.subjectProperties],
  );

  const columnDefs = useMemo(
    () => [
      {
        headerName: "Source",
        colId: "label",
        minWidth: 230,
        flex: 2,
        valueGetter: (p) => p.data?.label || p.data?.componentType || "—",
        cellRenderer: (p) => {
          const row = p.data || {};
          const meta = getComponentMeta(row.componentType, components);
          const unit = row.unitId ? unitsById.get(String(row.unitId)) : null;
          const isRent = row.componentType === GROSS_MONTHLY_RENT;
          const scope = unit
            ? `${unit.propertyLabel} · Unit ${unit.unitNumber}`
            : row.subjectPropertyId
              ? propertyLabelById.get(String(row.subjectPropertyId))
              : null;
          const basis = isRent ? rentBasisLabel(row.rentBasis) : null;
          const aboveMarket = leaseAboveMarket(row, unit);
          return (
            <Flx column g={0} sx={{ py: 0.5, minWidth: 0 }}>
              <Flx ac g={0.75} sx={{ minWidth: 0 }}>
                <Txt
                  sx={{
                    fontSize: 13,
                    fontWeight: 600,
                    lineHeight: 1.3,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {p.value}
                </Txt>
                {basis ? (
                  <Chip
                    size="small"
                    label={basis}
                    sx={{
                      height: 17,
                      fontSize: 10,
                      fontWeight: 700,
                      bgcolor: grey[100],
                      color: grey[700],
                      "& .MuiChip-label": { px: 0.75 },
                    }}
                  />
                ) : null}
                {isRent && !unit ? (
                  <Tooltip title="Every rent row names a unit — open the row and pick one.">
                    <Chip
                      size="small"
                      label="no unit"
                      sx={{
                        height: 17,
                        fontSize: 10,
                        fontWeight: 700,
                        bgcolor: amber[50],
                        color: amber[900],
                        "& .MuiChip-label": { px: 0.75 },
                      }}
                    />
                  </Tooltip>
                ) : null}
                {aboveMarket ? (
                  <Tooltip title="Lease rent is above the appraisal market rent — qualifies at the lease amount with the 'two months of rent payments verified' condition.">
                    <Chip
                      size="small"
                      label="lease > market"
                      sx={{
                        height: 17,
                        fontSize: 10,
                        fontWeight: 700,
                        bgcolor: amber[50],
                        color: amber[900],
                        "& .MuiChip-label": { px: 0.75 },
                      }}
                    />
                  </Tooltip>
                ) : null}
              </Flx>
              <Txt
                sx={{
                  fontSize: 11,
                  color: grey[600],
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {row.componentType}
                {unit
                  ? ` · Unit ${unit.unitNumber} · ${unit.propertyLabel}`
                  : scope && scope !== p.value
                    ? ` · ${scope}`
                    : ""}
                {meta.annual ? " · entered annually" : ""}
              </Txt>
            </Flx>
          );
        },
      },
      {
        // As entered — a /yr or /mo suffix so an annual row reads "as printed"
        // next to its monthly normalisation.
        headerName: "Amount",
        field: "amount",
        minWidth: 118,
        flex: 0.8,
        cellClass: "num",
        headerClass: "num",
        cellRenderer: (p) => {
          const annual = getComponentMeta(p.data?.componentType, components).annual;
          return (
            <Flx ac g={0.5} sx={{ height: "100%", justifyContent: "flex-end" }}>
              <Txt sx={{ fontSize: 12.5 }}>{formatDollar(p.value, "—")}</Txt>
              <Txt sx={{ fontSize: 10.5, color: grey[500] }}>{annual ? "/yr" : "/mo"}</Txt>
            </Flx>
          );
        },
      },
      {
        // Server-computed: Annual* components ÷ 12. Shown so an annual row's
        // contribution to the monthly math is visible next to what was entered.
        headerName: "Monthly",
        field: "monthlyAmount",
        minWidth: 100,
        flex: 0.7,
        cellClass: "num",
        headerClass: "num",
        valueFormatter: (p) => formatDollar(p.value, "—"),
      },
      {
        headerName: "Counted",
        field: "countedPercentage",
        minWidth: 90,
        flex: 0.6,
        cellClass: "num",
        headerClass: "num",
        valueFormatter: (p) => `${p.value == null ? 100 : p.value}%`,
      },
      {
        // Monthly, after the UW haircut — the figure that actually enters the
        // validated DSCR. Server-computed (`validatedMonthlyAmount`).
        headerName: "Validated",
        field: "validatedMonthlyAmount",
        minWidth: 110,
        flex: 0.8,
        cellClass: "num",
        headerClass: "num",
        cellStyle: { fontWeight: 700 },
        valueFormatter: (p) => formatDollar(p.value, "—"),
      },
      {
        headerName: "Verified",
        colId: "verified",
        minWidth: 84,
        flex: 0.55,
        cellClass: "ctr",
        headerClass: "ctr",
        // Icon-only cell → centered both axes (height:100% + jc/ac).
        cellRenderer: (p) => (
          <Flx ac jc sx={{ height: "100%" }}>
            {p.data?.verified ? (
              <Tooltip title="UW verified — counts toward the verified DSCR">
                <VerifiedOutlined sx={{ fontSize: 18, color: green[700] }} />
              </Tooltip>
            ) : (
              <Tooltip title="Not yet verified — excluded from the verified DSCR">
                <RadioButtonUncheckedOutlined
                  sx={{ fontSize: 18, color: grey[400] }}
                />
              </Tooltip>
            )}
          </Flx>
        ),
      },
      {
        headerName: "Documents",
        colId: "loanDocumentIds",
        minWidth: 170,
        flex: 1.1,
        sortable: false,
        valueGetter: (p) => (p.data?.loanDocumentIds || []).length,
        cellRenderer: (p) => {
          if (!sourceHasEvidence(p.data)) {
            // height:100% + ac keeps the flag vertically centered in the row.
            return (
              <Flx ac g={0.5} sx={{ height: "100%" }}>
                <WarningAmberOutlined
                  sx={{ fontSize: 16, color: amber[800] }}
                />
                <Txt sx={{ fontSize: 11.5, color: amber[900] }}>Missing</Txt>
              </Flx>
            );
          }
          return (
            <Flx
              ac
              g={0.5}
              sx={{ flexWrap: "wrap", height: "100%", py: 0.5 }}
            >
              {p.data.loanDocumentIds.map((id) => {
                const doc = docsById.get(String(id));
                return (
                  <Chip
                    key={id}
                    size="small"
                    icon={<DescriptionOutlined sx={{ fontSize: 13 }} />}
                    label={docLabel(doc)}
                    // Displayed file → always clickable + previewable. Opens the
                    // global file-preview sidebar; stop the row-click handler.
                    clickable={!!doc}
                    onClick={
                      doc
                        ? (e) => {
                            e.stopPropagation();
                            dispatch(filePreviewSetData(doc));
                          }
                        : undefined
                    }
                    sx={{
                      height: 20,
                      fontSize: 11,
                      maxWidth: 190,
                      cursor: doc ? "pointer" : "default",
                    }}
                  />
                );
              })}
            </Flx>
          );
        },
      },
      // No edit column — the row itself opens the drawer (owner ask 2026-09-16).
    ],
    [components, docsById, unitsById, propertyLabelById, dispatch],
  );

  const defaultColDef = useMemo(() => CARD_GRID_COL_DEF, []);

  return (
    <Box sx={TABLE_BOX_SX}>
      {/* The Admin → DSCR Pricing table look (`CARD_GRID_SX`); the only local
          addition is a row tall enough for the two-line Source cell. */}
      <AgGridTableWrapper
        compactTable
        noBorder
        suppressMinHeightWhenPopulated={sources}
        sx={CARD_GRID_SX}
      >
        <AgGridReact
          rowData={sources}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          getRowId={(p) => String(p.data?._id ?? p.data?.__tmpId)}
          domLayout="autoHeight"
          rowHeight={CARD_GRID_TWO_LINE_ROW_H}
          // Owner ask (2026-09-16): the whole row opens the drawer, not only the
          // pinned edit button. Safe here because the only in-cell control (the
          // document chip) stops propagation before the row handler runs.
          onRowClicked={(e) => onRowClick?.(e.data)}
          getRowStyle={(p) => ({
            cursor: "pointer",
            ...(String(p.data?._id) === String(activeSourceId)
              ? CARD_GRID_ACTIVE_ROW_STYLE
              : {}),
          })}
        />
      </AgGridTableWrapper>
    </Box>
  );
};

DscrSourcesTable.propTypes = {
  sources: PropTypes.array,
  loan: PropTypes.object,
  components: PropTypes.array,
  onRowClick: PropTypes.func,
  activeSourceId: PropTypes.string,
};

export default DscrSourcesTable;
